import type { UnderperformerRow } from '@monitoring-panel/types';
import { AlertTriangle } from 'lucide-react';
import { ReportTable, type ReportColumn } from './ReportTable';
import { formatCount, formatInt, formatPercent } from '@/lib/report-format';

interface ReportUnderperformersProps {
  rows: UnderperformerRow[];
}

/**
 * Watchlist of flagged URLs from `underperformers`, highlighting wasted
 * impressions and the reasons each URL was flagged.
 */
export function ReportUnderperformers({ rows }: ReportUnderperformersProps) {
  const totalWasted = rows.reduce((sum, r) => sum + (r.wasted_impressions || 0), 0);

  const columns: ReportColumn<UnderperformerRow>[] = [
    {
      key: 'url',
      label: 'URL',
      wrap: true,
      render: (r) => (
        <div className="min-w-0">
          <span className="font-bold text-adv-primary">{r.domain}</span>
          <a
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="adv-link block text-adv-body-s break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {r.url}
          </a>
        </div>
      ),
    },
    { key: 'impressions_count', label: 'Impressions', align: 'right', render: (r) => formatCount(r.impressions_count) },
    { key: 'ctr', label: 'CTR', align: 'right', render: (r) => formatPercent(r.ctr) },
    { key: 'viewability_rate', label: 'Viewability', align: 'right', render: (r) => formatPercent(r.viewability_rate) },
    { key: 'total_conversions', label: 'Conv.', align: 'right', render: (r) => formatInt(r.total_conversions) },
    {
      key: 'wasted_impressions',
      label: 'Wasted',
      align: 'right',
      render: (r) => <span className="text-adv-warning-text font-bold">{formatCount(r.wasted_impressions)}</span>,
    },
    {
      key: 'flags',
      label: 'Flags',
      csvValue: (r) =>
        [
          r.no_engagement ? 'No engagement' : '',
          r.low_viewability ? 'Low viewability' : '',
          !r.no_engagement && !r.low_viewability ? r.flags : '',
        ]
          .filter(Boolean)
          .join('; '),
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.no_engagement && <Flag label="No engagement" />}
          {r.low_viewability && <Flag label="Low viewability" />}
          {!r.no_engagement && !r.low_viewability && r.flags && <Flag label={r.flags} />}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="adv-alert flex items-center gap-2 py-2">
        <AlertTriangle size={16} className="flex-shrink-0" />
        <span>
          <span className="adv-num font-bold">{rows.length}</span> flagged URL{rows.length === 1 ? '' : 's'} ·{' '}
          <span className="adv-num font-bold">{formatCount(totalWasted)}</span> wasted impressions
        </span>
      </div>
      <ReportTable rows={rows} columns={columns} initialLimit={25} emptyLabel="No underperformers flagged" />
    </div>
  );
}

function Flag({ label }: { label: string }) {
  return (
    <span className="adv-tag adv-tag--warning">
      {label}
    </span>
  );
}
